'use client'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { gameQueries } from '@/lib/queries'
import { DollarSign, Loader2, RefreshCw } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'

interface ModelPricingEntry {
  input: number
  output: number
  source: 'direct' | 'openrouter'
}

type PricingData = Record<string, ModelPricingEntry>
type ProviderSources = Record<string, 'direct' | 'openrouter'>

/**
 * Format a per-token price (in USD) as a per-million-token price string.
 * E.g. 0.000001 → "$1.00"
 */
function formatPerMillion(pricePerToken: number): string {
  const perMillion = pricePerToken * 1_000_000
  if (perMillion === 0)
    return '$0.00'
  if (perMillion < 0.01)
    return `$${perMillion.toFixed(4)}`
  return `$${perMillion.toFixed(2)}`
}

/**
 * Group models by provider prefix (e.g. "openai/gpt-5" → "openai")
 */
function groupByProvider(pricing: PricingData): Record<string, Array<{ model: string, input: number, output: number, source: 'direct' | 'openrouter' }>> {
  const groups: Record<string, Array<{ model: string, input: number, output: number, source: 'direct' | 'openrouter' }>> = {}

  for (const [modelName, prices] of Object.entries(pricing)) {
    const slashIdx = modelName.indexOf('/')
    const provider = slashIdx > 0 ? modelName.slice(0, slashIdx) : 'other'

    if (!groups[provider]) {
      groups[provider] = []
    }
    groups[provider].push({
      model: modelName,
      input: prices.input,
      output: prices.output,
      source: prices.source,
    })
  }

  // Sort providers alphabetically, and models within each provider
  const sorted: Record<string, Array<{ model: string, input: number, output: number, source: 'direct' | 'openrouter' }>> = {}
  for (const key of Object.keys(groups).sort()) {
    sorted[key] = groups[key].sort((a, b) => a.model.localeCompare(b.model))
  }

  return sorted
}

const PROVIDER_LABELS: Record<string, string> = {
  'openai': 'OpenAI',
  'google': 'Google',
  'anthropic': 'Anthropic',
  'x-ai': 'xAI',
  'deepseek': 'DeepSeek',
  'z-ai': 'Z-AI (GLM)',
  'moonshotai': 'Moonshot AI',
}

const PROVIDER_COLORS: Record<string, string> = {
  'openai': 'from-emerald-500/20 to-emerald-600/10 border-emerald-500/30',
  'google': 'from-blue-500/20 to-blue-600/10 border-blue-500/30',
  'anthropic': 'from-orange-500/20 to-orange-600/10 border-orange-500/30',
  'x-ai': 'from-purple-500/20 to-purple-600/10 border-purple-500/30',
  'deepseek': 'from-cyan-500/20 to-cyan-600/10 border-cyan-500/30',
  'z-ai': 'from-red-500/20 to-red-600/10 border-red-500/30',
  'moonshotai': 'from-yellow-500/20 to-yellow-600/10 border-yellow-500/30',
}

export function TokenCostSection() {
  const [pricing, setPricing] = useState<PricingData | null>(null)
  const [providerSources, setProviderSources] = useState<ProviderSources>({})
  const [loading, setLoading] = useState(true)
  const [syncing, setSyncing] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)
  const [lastSynced, setLastSynced] = useState<string | null>(null)

  const loadPricing = useCallback(async () => {
    setLoading(true)
    try {
      const result = await gameQueries.getModelPricing()
      if (result.success && result.data) {
        setPricing(result.data)
        setProviderSources(result.providerSources || {})
      }
    }
    catch (error) {
      console.error('Failed to load pricing:', error)
      setMessage({ type: 'error', text: 'Failed to load pricing data' })
    }
    finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadPricing()
  }, [loadPricing])

  const handleSync = async () => {
    setSyncing(true)
    setMessage(null)
    try {
      const result = await gameQueries.syncModelPricing()
      if (result.success && result.data) {
        setPricing(result.data)
        setProviderSources(result.providerSources || {})
        setLastSynced(new Date().toLocaleString())
        setMessage({ type: 'success', text: result.message })
      }
    }
    catch (error) {
      console.error('Failed to sync pricing:', error)
      setMessage({ type: 'error', text: `Failed to sync: ${error instanceof Error ? error.message : 'Unknown error'}` })
    }
    finally {
      setSyncing(false)
    }
  }

  const grouped = pricing ? groupByProvider(pricing) : {}
  const totalModels = pricing ? Object.keys(pricing).length : 0
  const modelsWithPricing = pricing
    ? Object.values(pricing).filter(p => p.input > 0 || p.output > 0).length
    : 0

  return (
    <div className="bg-slate-50 px-4 py-8 dark:bg-slate-900 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-5xl">
        <div className="mb-8 text-center">
          <h2 className="mb-2 text-xl font-bold text-foreground md:text-2xl lg:text-3xl">
            Token Cost
          </h2>
          <p className="text-sm text-muted-foreground">
            Fetch and view AI model pricing. Prices are per million tokens in USD.
          </p>
        </div>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <DollarSign className="size-5" />
                Model Pricing ({modelsWithPricing}/{totalModels} models with pricing)
              </div>
              <Button
                onClick={handleSync}
                disabled={syncing}
                size="sm"
                className="gap-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:from-blue-600 hover:to-indigo-700"
              >
                {syncing ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <RefreshCw className="size-4" />
                )}
                {syncing ? 'Syncing...' : 'Sync Pricing'}
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {message && (
              <p
                className={`rounded-md px-3 py-2 text-sm ${
                  message.type === 'success'
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                    : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                }`}
              >
                {message.text}
              </p>
            )}

            {lastSynced && (
              <p className="text-xs text-muted-foreground">
                Last synced: {lastSynced}
              </p>
            )}

            {loading ? (
              <div className="flex items-center justify-center gap-2 py-8 text-muted-foreground">
                <Loader2 className="size-5 animate-spin" />
                Loading pricing data...
              </div>
            ) : Object.keys(grouped).length > 0 ? (
              <div className="space-y-6">
                {Object.entries(grouped).map(([provider, models]) => {
                  const source = providerSources[provider] || (models[0]?.source ?? 'openrouter')
                  const sourceLabel = source === 'direct'
                    ? `${PROVIDER_LABELS[provider] || provider} Official API`
                    : 'via OpenRouter'

                  return (
                    <div
                      key={provider}
                      className={`rounded-lg border bg-gradient-to-br p-4 ${
                        PROVIDER_COLORS[provider] || 'border-gray-500/30 from-gray-500/20 to-gray-600/10'
                      }`}
                    >
                      <div className="mb-3 flex items-center justify-between">
                        <h3 className="text-sm font-semibold uppercase tracking-wider text-foreground">
                          {PROVIDER_LABELS[provider] || provider}
                          <span className="ml-2 text-xs font-normal text-muted-foreground">
                            ({models.length} models)
                          </span>
                        </h3>
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                            source === 'direct'
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                              : 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300'
                          }`}
                        >
                          {source === 'direct' ? '🔑 ' : '🔄 '}
                          {sourceLabel}
                        </span>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-border/50">
                              <th className="pb-2 text-left font-medium text-muted-foreground">Model</th>
                              <th className="pb-2 text-right font-medium text-muted-foreground">Input / 1M tokens</th>
                              <th className="pb-2 text-right font-medium text-muted-foreground">Output / 1M tokens</th>
                            </tr>
                          </thead>
                          <tbody>
                            {models.map(({ model, input, output }) => {
                              const hasPrice = input > 0 || output > 0
                              return (
                                <tr
                                  key={model}
                                  className="border-b border-border/20 last:border-0"
                                >
                                  <td className="py-2 font-mono text-xs text-foreground">
                                    {model.split('/')[1] || model}
                                  </td>
                                  <td className={`py-2 text-right font-mono text-xs ${hasPrice ? 'text-foreground' : 'text-muted-foreground'}`}>
                                    {hasPrice ? formatPerMillion(input) : '—'}
                                  </td>
                                  <td className={`py-2 text-right font-mono text-xs ${hasPrice ? 'text-foreground' : 'text-muted-foreground'}`}>
                                    {hasPrice ? formatPerMillion(output) : '—'}
                                  </td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className="py-8 text-center text-muted-foreground">
                <p className="mb-2">No pricing data available.</p>
                <p className="text-sm">Click &quot;Sync Pricing&quot; to fetch the latest pricing.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
